import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Spinner } from '~/components/Spinner';
import type { TerritoryCode } from '~/lib/territories/territories';
import { DataUnavailable } from '~/modules/artist-roster/components/DataUnavailable/DataUnavailable';
import { getArtistTracksPerformance } from '~/modules/artist-roster/lib/tracks';
import type { AssetFamily } from '~/modules/artist-roster/lib/types';
import { formatCount } from '~/modules/artist-roster/lib/utils';

import { DateRangePicker } from './components/DateRangePicker/DateRangePicker';
import { TerritoryPicker } from './components/TerritoryPicker/TerritoryPicker';
import { PerformanceModules } from './PerformanceModules';
import { PerformanceSelectorCard } from './PerformanceSelectorCard';

interface PerformanceViewProps {
  trackData: AssetFamily;
}

export type MetricType =
  | 'streamsRTD'
  | 'periodStreams'
  | 'organicStreams'
  | 'socialCreations';

const TODAY = new Date();

TODAY.setHours(0, 0, 0, 0);

export function PerformanceView({ trackData }: PerformanceViewProps) {
  const { id: familyId, initialReleaseDate } = trackData;
  const { t } = useTranslation('roster');

  const [selectedMetricType, setSelectedMetricType] =
    useState<MetricType>('streamsRTD');

  const [dateRange, setDateRange] = useState<DateRange>({
    from: addDays(TODAY, -((TODAY.getDay() + 2) % 7)),
    to: TODAY,
  });

  const [selectedTerritories, setSelectedTerritories] = useState<
    TerritoryCode[]
  >([]);

  const {
    data: performancesMap,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['artist-tracks-performance', familyId, dateRange],
    queryFn: () => {
      if (familyId) {
        return getArtistTracksPerformance({
          familyIds: [familyId],
          startDate: dateRange.from
            ? format(dateRange.from, 'yyyy-MM-dd')
            : undefined,
          endDate: dateRange.to
            ? format(dateRange.to, 'yyyy-MM-dd')
            : undefined,
        });
      }

      return Promise.resolve(null);
    },
    enabled: !!familyId,
    placeholderData: keepPreviousData,
  });

  const performance = useMemo(() => {
    if (!performancesMap || !familyId) {
      return null;
    }

    return performancesMap.get(familyId);
  }, [familyId, performancesMap]);

  const handleTerritoryApplyClick = useCallback(
    (territories: TerritoryCode[]) => {
      setSelectedTerritories(territories);
    },
    [],
  );

  if (isFetching) {
    return (
      <div className="flex w-full items-center justify-center p-2xl">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  if (!performance) {
    return null;
  }

  return (
    <div className="flex w-full flex-col items-start gap-lg">
      <div className="flex w-full items-center justify-start gap-sm">
        {initialReleaseDate ? (
          <DateRangePicker
            onApply={(range) => setDateRange(range)}
            value={dateRange}
            initialReleaseDate={new Date(initialReleaseDate)}
          />
        ) : null}
        <TerritoryPicker
          initialSelectedTerritories={selectedTerritories}
          onApply={handleTerritoryApplyClick}
        />
      </div>
      <div className="flex w-full gap-sm">
        <PerformanceSelectorCard
          title={t('trackDetails.labels.streamsRTD')}
          value={formatCount(performance.streaming?.aggregated?.totalStreams)}
          isActive={selectedMetricType === 'streamsRTD'}
          onClick={() => setSelectedMetricType('streamsRTD')}
        />
        <PerformanceSelectorCard
          title={t('trackDetails.labels.periodStreams')}
          value={formatCount(
            performance.streaming?.aggregated?.periodStreams?.current,
          )}
          delta={performance.streaming?.aggregated?.periodStreams?.deltaRatio}
          isActive={selectedMetricType === 'periodStreams'}
          onClick={() => setSelectedMetricType('periodStreams')}
        />
        <PerformanceSelectorCard
          title={t('trackDetails.labels.organicStreams')}
          value={formatCount(
            performance.streaming?.aggregated?.organicStreams?.current,
          )}
          delta={performance.streaming?.aggregated?.organicStreams?.deltaRatio}
          isActive={selectedMetricType === 'organicStreams'}
          onClick={() => setSelectedMetricType('organicStreams')}
        />
        <PerformanceSelectorCard
          title={t('trackDetails.labels.socialCreations')}
          value={formatCount(
            performance.engagement?.aggregated?.socialCreations?.current,
          )}
          delta={
            performance.engagement?.aggregated?.socialCreations?.deltaRatio
          }
          isActive={selectedMetricType === 'socialCreations'}
          onClick={() => setSelectedMetricType('socialCreations')}
        />
      </div>

      {/* Display different performance modules based on the metric type */}
      <PerformanceModules
        familyId={familyId}
        metricType={selectedMetricType}
        performance={performance}
      />
    </div>
  );
}

PerformanceView.displayName = 'PerformanceView';
